import { lazy, type ComponentType } from "react";
import { isChunkLoadError, reloadOnceForStaleChunk } from "./chunkReload";

/**
 * `React.lazy` for modules that use a NAMED export (every page in this app does,
 * e.g. `export function DashboardPage()`). `React.lazy` requires the dynamic
 * import to resolve to `{ default }`, so this adapts a named export into that
 * shape — keeping route definitions readable (US-044 AC11):
 *
 *   const DashboardPage = lazyNamed(() => import("@/pages/DashboardPage"), "DashboardPage");
 *
 * Each call site is a distinct `import(...)`, so Vite/Rollup code-splits every
 * page into its own chunk — an auditor's entry path never loads admin/staff
 * page code.
 */
export function lazyNamed<M, K extends keyof M>(
  loader: () => Promise<M>,
  name: K,
) {
  return lazy(() =>
    loader()
      .then((module) => {
        // Belt-and-braces: a swallowed preload error (any preventDefault'd
        // vite:preloadError) makes the import RESOLVE with undefined. Convert
        // that back into a chunk-load error so it takes the recovery path
        // below instead of crashing on module[name].
        if (!module || !module[name]) {
          throw new Error(
            "Failed to fetch dynamically imported module (resolved empty)",
          );
        }
        return { default: module[name] as ComponentType<unknown> };
      })
      .catch((error) => {
        // A stale chunk hash (deploy replaced it / ALB hit a different-version
        // task) can never resolve on retry — only a fresh index.html recovers.
        // Reload once; rethrow so React's boundary holds until the reload lands.
        // Non-chunk errors (a real bug in the page module) rethrow untouched so
        // they surface normally instead of triggering a useless reload loop.
        if (isChunkLoadError(error)) reloadOnceForStaleChunk();
        throw error;
      }),
  );
}
